"use client";
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import CardChevron from "@/Components/Common/CardChevron";
import { onActivateKey } from "@/Components/OnwardTravel/onActivateKey";
import { useIntl } from "react-intl";
import { IPassengerSelection } from "../../Orchestrator/Types/Interface/IPassenger";
import { getSearchSelectedPassenger } from "../../Lib/Utils/passenger";
import { getMaxAllowedInfants, isInfantIncrementDisabled, isChildIncrementDisabled } from "../../Lib/Utils/passengerValidation";
import ItemContent from "../LandingPage/PassengerSelector/ItemContent";
import "../../Components/LandingPage/PassengerSelector/search-trip.css";
import "./style.css";

interface GroupBookingPassengerSelectorProps {
  fieldId: string;
  value: string;
  onChange: (value: string) => void;
  passengerTypes: IPassengerSelection[];
  minPassenger: number | string;
}

const GroupBookingPassengerSelector: React.FC<GroupBookingPassengerSelectorProps> = ({
  fieldId: _fieldId,
  value,
  onChange,
  passengerTypes,
  minPassenger
}) => {
  const intl = useIntl();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse passenger data from JSON string or default to empty object
  const getPassengers = useCallback((): Record<string, number> => {
    if (!value) return {};
    try {
      const parsed = JSON.parse(value);
      return parsed || {};
    } catch {
      return {};
    }
  }, [value]);

  const [passengers, setPassengers] = useState<Record<string, number>>(getPassengers());

  // Update passengers when value changes externally
  useEffect(() => {
    setPassengers(getPassengers());
  }, [value, getPassengers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // TODO: Route through EventBus → GroupBookingPassengerSelectionAction so this
  // component no longer contains business logic inline. Until that refactor lands,
  // the validation below MUST stay in sync with passengerValidation.ts utilities.
  // See: passengerValidation.ts — getMaxAllowedInfants / isChildIncrementDisabled
  const handlePassengerChange = useCallback(
    (type: string, delta: number) => {
      setPassengers((prev) => {
        const currentCount = prev[type] || 0;
        const newCount = Math.max(0, currentCount + delta);

        // Check if incrementing would violate rules
        if (delta > 0) {
          // Check infant limit: ONE_ADULT_OR_SENIOR_PER_INFANT
          if (type === "infants") {
            const maxAllowed = getMaxAllowedInfants(prev);
            if (newCount > maxAllowed) {
              console.warn("[GroupBookingPassengerSelector] Infant increment blocked — exceeds adult+senior count", { newCount, maxAllowed, prev });
              return prev;
            }
          }

          // Check child rule: ONE_ADULT_OR_SENIOR
          if (type === "children" && isChildIncrementDisabled(prev)) {
            console.warn("[GroupBookingPassengerSelector] Child increment blocked — no adults or seniors present", { prev });
            return prev;
          }
        }

        const updated = {
          ...prev,
          [type]: newCount,
        };

        // Remove zero counts for cleaner data
        Object.keys(updated).forEach((key) => {
          if (updated[key] === 0) {
            delete updated[key];
          }
        });

        // Save to form data (can be empty object if no passengers selected)
        onChange(JSON.stringify(updated));

        return updated;
      });
    },
    [onChange]
  );

  const { allPassengerSummary } = getSearchSelectedPassenger(
    Object.keys(passengers).length > 0 ? passengers : {},
    intl
  );
  
  // Show placeholder when no passengers selected
  const displayText = Object.keys(passengers).length > 0 
    ? (allPassengerSummary || intl.formatMessage({ id: "passengers" }))
    : intl.formatMessage({ id: "select" }) + " " + intl.formatMessage({ id: "passengers" });

  const isIncrementDisabled = (type: IPassengerSelection) => {
    return (
      (type.id === "infants" && isInfantIncrementDisabled(passengers || {})) ||
      (type.id === "children" && isChildIncrementDisabled(passengers || {}))
    );
  };

  return (
    <div className="group-booking-passenger-selector-wrapper" ref={dropdownRef}>
      <div
        className="group-booking-passenger-selector"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        onKeyDown={onActivateKey(() => setIsDropdownOpen(!isDropdownOpen))}
        role="button"
        tabIndex={0}
        aria-expanded={isDropdownOpen}
        aria-label="passenger-selector"
      >
        <span 
          className={Object.keys(passengers).length === 0 ? "passenger-placeholder" : "passenger-value"}
          title={allPassengerSummary || displayText}
        >
          {displayText}
        </span>
        <span className="arrow">
          <CardChevron direction="down" width={12} height={12} />
        </span>
      </div>
      {isDropdownOpen && (
        <div className="passenger-dropdown group-booking-passenger-dropdown">
          {passengerTypes.map((type) => (
            <Fragment key={type.id}>
              <ItemContent
                type={type}
                currentCount={passengers?.[type.id] || 0}
                handlePassengerChange={handlePassengerChange}
                searchPassengers={passengers}
                isDecrementDisabled={
                  passengers?.[type.id] === 0 || passengers?.[type.id] === undefined
                }
                isIncrementDisabled={isIncrementDisabled(type)}
              />
            </Fragment>
          ))}

          <div className="total-passengers-limit">
            <p>{intl.formatMessage({ id: "groupBookingMinPassengers" }, { minPassenger })}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupBookingPassengerSelector;

